type OpeningPerfEvent =
  | 'open-project:start'
  | 'project:detail-fetch-start'
  | 'project:detail-fetch-end'
  | 'project:bounds-ready'
  // Eventos usados en otros componentes (pre-existentes, faltaban en el tipo)
  | 'panel:networks-and-grids-mount'
  | 'network:has-network-true'
  | 'map:revealed'
  // Fase 2C: Bootstrap cache
  | 'project-bootstrap-cache:hit'
  | 'project-bootstrap-cache:miss'
  | 'project-bootstrap-cache:invalid'
  | 'project-bootstrap-cache:write'
  | 'map:create-from-cached-bounds'
  | 'map:reconcile-project-detail'
  | 'map:fitbounds-from-real-project'
  | 'stats:late-confirmed'
  | 'stats:late-empty'
  // Mapa
  | 'map:create-start'
  | 'map:create-end'
  | 'map:first-idle'
  | 'network:stats-fetch-start'
  | 'network:stats-fetch-end'
  | 'network:has-data-ready'
  | 'network:has-data-optimistic'
  | 'network:map-ready-gate-passed'
  | 'network:load-start'
  | 'network:fetch-end'
  | 'network:sync-end'
  | 'network:fade-start'
  | 'network:fade-end'
  | 'ui:tools-enabled'
  // Fase 2D: eventos desacoplados de red (mantener para compat)
  | 'ui:map-interactive-ready'
  | 'ui:network-preview-ready'
  | 'ui:full-network-ready'
  // Fase 2D.2: tres niveles de readiness del mapa
  | 'map:style-ready'
  | 'ui:map-shell-ready'
  | 'ui:map-style-ready'
  | 'ui:map-visual-ready'
  | 'ui:basic-tools-enabled';

interface OpeningPerfSession {
  id: string;
  projectId: string;
  startedAt: number;
  marks: Partial<Record<OpeningPerfEvent, number>>;
  completed: boolean;
}

const EVENT_ORDER: OpeningPerfEvent[] = [
  'open-project:start',
  'project-bootstrap-cache:hit',
  'project-bootstrap-cache:miss',
  'project-bootstrap-cache:invalid',
  'map:create-from-cached-bounds',
  'project:detail-fetch-start',
  'project:detail-fetch-end',
  'project-bootstrap-cache:write',
  'project:bounds-ready',
  'map:reconcile-project-detail',
  'map:fitbounds-from-real-project',
  'stats:late-confirmed',
  'stats:late-empty',
  'map:create-start',
  'map:create-end',
  'map:first-idle',
  'network:stats-fetch-start',
  'network:stats-fetch-end',
  'network:has-data-ready',
  'network:has-data-optimistic',
  'network:map-ready-gate-passed',
  'network:load-start',
  'network:fetch-end',
  'network:sync-end',
  'network:fade-start',
  'network:fade-end',
  // Fase 2D.2: readiness del mapa (orden cronológico esperado)
  'ui:map-shell-ready',
  'ui:basic-tools-enabled',
  'ui:tools-enabled',
  'map:style-ready',
  'ui:map-style-ready',
  'ui:network-preview-ready',
  'ui:map-interactive-ready', // compat
  'ui:map-visual-ready',
  'ui:full-network-ready',
];

const OPENING_PERF_PREFIX = '[OPENING-PERF]';
const sessions = new Map<string, OpeningPerfSession>();
let activeSessionId: string | null = null;
let sessionCounter = 0;

function formatMs(value: number | null): string {
  return value === null ? 'n/a' : `${Math.round(value)} ms`;
}

function getRelativeTime(session: OpeningPerfSession, event: OpeningPerfEvent): number | null {
  const timestamp = session.marks[event];
  if (typeof timestamp !== 'number') {
    return null;
  }

  return timestamp - session.startedAt;
}

function getDuration(
  session: OpeningPerfSession,
  startEvent: OpeningPerfEvent,
  endEvent: OpeningPerfEvent
): number | null {
  const start = session.marks[startEvent];
  const end = session.marks[endEvent];

  if (typeof start !== 'number' || typeof end !== 'number') {
    return null;
  }

  return end - start;
}

function printSummary(session: OpeningPerfSession): void {
  console.groupCollapsed(`${OPENING_PERF_PREFIX} session=${session.id} SUMMARY`);
  EVENT_ORDER.forEach((event) => {
    const relativeTime = getRelativeTime(session, event);
    if (relativeTime !== null) {
      console.log(`- raw ${event}: ${formatMs(relativeTime)}`);
    }
  });
  // ── Fase 2D.2: duraciones clave ──
  console.log(`- [2D.2] open → map shell (basic tools): ${formatMs(getDuration(session, 'open-project:start', 'ui:map-shell-ready'))}`);
  console.log(`- [2D.2] open → map style ready:         ${formatMs(getDuration(session, 'open-project:start', 'ui:map-style-ready'))}`);
  console.log(`- [2D.2] open → network preview:         ${formatMs(getDuration(session, 'open-project:start', 'ui:network-preview-ready'))}`);
  console.log(`- [2D.2] open → map visual (tiles):      ${formatMs(getDuration(session, 'open-project:start', 'ui:map-visual-ready'))}`);
  console.log(`- [2D.2] open → full network ready:      ${formatMs(getDuration(session, 'open-project:start', 'ui:full-network-ready'))}`);
  // ── Métricas clásicas ──
  console.log(`- total open → tools enabled: ${formatMs(getDuration(session, 'open-project:start', 'ui:tools-enabled'))}`);
  console.log(`- project detail fetch: ${formatMs(getDuration(session, 'project:detail-fetch-start', 'project:detail-fetch-end'))}`);
  console.log(`- bounds ready: ${formatMs(getRelativeTime(session, 'project:bounds-ready'))}`);
  console.log(`- network stats fetch: ${formatMs(getDuration(session, 'network:stats-fetch-start', 'network:stats-fetch-end'))}`);
  console.log(`- map create → first idle: ${formatMs(getDuration(session, 'map:create-start', 'map:first-idle'))}`);
  console.log(`- map-ready gate passed → network load start: ${formatMs(getDuration(session, 'network:map-ready-gate-passed', 'network:load-start'))}`);
  console.log(`- hasData ready → network load start: ${formatMs(getDuration(session, 'network:has-data-ready', 'network:load-start'))}`);
  console.log(`- network load → fetch end: ${formatMs(getDuration(session, 'network:load-start', 'network:fetch-end'))}`);
  console.log(`- network load → sync end: ${formatMs(getDuration(session, 'network:load-start', 'network:sync-end'))}`);
  console.log(`- network sync → fade start: ${formatMs(getDuration(session, 'network:sync-end', 'network:fade-start'))}`);
  console.log(`- network fade duration: ${formatMs(getDuration(session, 'network:fade-start', 'network:fade-end'))}`);
  console.groupEnd();
}

function maybeCompleteSession(session: OpeningPerfSession): void {
  if (session.completed) return;

  const hasToolsEnabled = typeof session.marks['ui:tools-enabled'] === 'number';
  if (!hasToolsEnabled) return;

  const hasSyncEnd = typeof session.marks['network:sync-end'] === 'number';
  const hasFadeEnd = typeof session.marks['network:fade-end'] === 'number';
  const hasFullNetworkReady = typeof session.marks['ui:full-network-ready'] === 'number';

  // Classic path (pre–Fase 2D): sync happened but fade not yet — keep session open
  if (hasSyncEnd && !hasFadeEnd) return;

  // Fase 2D.2 path: tools enabled early (before sync/fade).
  // Keep session open until full-network-ready so SUMMARY captures all timings.
  if (!hasSyncEnd && !hasFullNetworkReady) return;

  session.completed = true;
  printSummary(session);

  if (activeSessionId === session.id) {
    activeSessionId = null;
  }
}

function createSessionId(projectId: string): string {
  sessionCounter += 1;
  const projectToken = projectId.slice(0, 8) || 'unknown';
  return `${projectToken}-${Date.now().toString(36)}-${sessionCounter}`;
}

function pruneSessions(): void {
  const MAX_SESSIONS = 12;
  const sessionIds = Array.from(sessions.keys());

  if (sessionIds.length <= MAX_SESSIONS) {
    return;
  }

  const idsToDelete = sessionIds.slice(0, sessionIds.length - MAX_SESSIONS);
  idsToDelete.forEach((sessionId) => {
    sessions.delete(sessionId);
  });
}

export function startOpeningPerfSession(projectId: string): string {
  const startedAt = performance.now();
  const sessionId = createSessionId(projectId);
  const session: OpeningPerfSession = {
    id: sessionId,
    projectId,
    startedAt,
    marks: {
      'open-project:start': startedAt,
    },
    completed: false,
  };

  activeSessionId = sessionId;
  sessions.set(sessionId, session);
  pruneSessions();

  console.log(`${OPENING_PERF_PREFIX} session=${sessionId} event=open-project:start t=0ms`);

  return sessionId;
}

export function getActiveOpeningPerfSessionId(expectedProjectId?: string | null): string | null {
  if (!activeSessionId) {
    return null;
  }

  const session = sessions.get(activeSessionId);
  if (!session) {
    return null;
  }

  if (expectedProjectId && session.projectId !== expectedProjectId) {
    return null;
  }

  return activeSessionId;
}

export function markOpeningPerfEvent(
  sessionId: string | null,
  event: OpeningPerfEvent
): void {
  if (!sessionId) {
    return;
  }

  const session = sessions.get(sessionId);
  if (!session || session.completed || activeSessionId !== sessionId) {
    return;
  }

  if (typeof session.marks[event] === 'number') {
    return;
  }

  const timestamp = performance.now();
  session.marks[event] = timestamp;

  const relativeTime = Math.round(timestamp - session.startedAt);
  console.log(`${OPENING_PERF_PREFIX} session=${session.id} event=${event} t=${relativeTime}ms`);

  maybeCompleteSession(session);
}
